import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Grid, Paper, Typography, Box } from "@mui/material";
import DynamicForm from "../../components/DynamicForm";
import DocumentEditor from "../../components/documentEditor/documentEditor";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@material-ui/core/styles";
import Loading from "../../../../components/loading";
import { toast, ToastContainer } from "react-toastify";
import { useFormCollaboration } from "../../hooks/useFormCollaboration";
import { FieldModel } from "../../types/docform.type";
import { getDocFormFillerAllFields } from "../../../../services/templateV2Service";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0),
    height: "calc(100vh - 148px)",
    backgroundColor: "#EFF3F7",
  },
  headerContainer: {
    color: "#0076FF",
    padding: theme.spacing(1),
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0),
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "32px",
    color: "#0076FF",
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(1),
  },
  container: {
    height: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "21px",
  },
  pdfContainer: {
    height: "100%",
    overflow: "hidden",
    width: "100%",
    borderRadius: "1px",
  },
  connectionStatus: {
    padding: "8px 16px",
    borderRadius: "4px",
    marginBottom: "16px",
    textAlign: "center",
    fontWeight: 500,
  },
  connected: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  disconnected: {
    backgroundColor: "#f44336",
    color: "white",
  },
}));

function Header({ isConnected }: { isConnected: boolean }) {
  const classes = useStyles();
  return (
    <div className={classes.headerContainer}>
      <h2 className={classes.headerTitle}>Live View</h2>
      <div className={`${classes.connectionStatus} ${isConnected ? classes.connected : classes.disconnected}`}>
        {isConnected ? "Connected - Viewing live updates" : "Connecting..."}
      </div>
    </div>
  );
}

function DocFormViewer() {
  const { id, idRequest } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [fields, setFields] = useState<FieldModel[]>([]);
  const [urlPdf, setUrlPdf] = useState("");
  const [values, setValues] = useState<any[]>([]);
  const [flowId, setFlowId] = useState<string>("");

  const setValue = useCallback((fieldId: string, value: string) => {
    setValues((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, value } : f))
    );
  }, []);

  const handleFieldChanged = useCallback((fieldId: string, fieldValue: string, userId: string) => {
    setValue(fieldId, fieldValue);
  }, [setValue]);

  const handleFlowCompleted = useCallback((payload: { flowId?: string; message?: string; timestamp?: number }) => {
    const message = payload?.message || "The flow has been completed";
    toast.info(message);
    navigate("/templates-v2");
  }, [navigate]);

  const { isConnected, error } = useFormCollaboration(idRequest ?? "", handleFieldChanged, flowId, handleFlowCompleted);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setLoadingMessage("Loading form...");

      const flowIdParam = searchParams.get("flowId");
      if (flowIdParam) {
        setFlowId(flowIdParam);
      }

      const response = await getDocFormFillerAllFields(id);

      if (!flowIdParam && response.data.flowId) {
        setFlowId(response.data.flowId);
      }

      const mappedFields = (response.data.new.form[0].fields || []).map((field: any) => ({
        ...field,
        id: field.fieldId,
        title: field.fieldDescription || field.title || field.fieldId
      }));
      setFields(mappedFields);

      const pdfUrlParam = searchParams.get("urlPdf");
      const pdfUrl = pdfUrlParam || response.data.pdfUrl || response.data.doc.thumbnail;
      setUrlPdf(pdfUrl);

      const initialValues = (response.data.new.form[0].fields || []).map((field: any) => ({
        id: field.fieldId,
        label: field.fieldDescription,
        placeholder: "",
        value: field.value || "",
        fieldType: field.fieldType,
      }));
      setValues(initialValues);
    } catch (error) {
      console.error("Error fetching form data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <ToastContainer autoClose={3000} />
      <Loading show={loading} text={loadingMessage} />

      <Header isConnected={isConnected} />
      <div className={classes.container}>
        <div className={classes.pdfContainer}>
          {urlPdf && fields.length > 0 && (
            <DocumentEditor
              fileUrl={urlPdf}
              fields={fields}
              onFieldChange={() => {}}
              fieldValues={values}
              readOnly={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default DocFormViewer;
